/**
 * MyClientsList (LP-11): the left column of My Clients. Every one of the
 * viewer's clients, alphabetical by first name (as All clients), no grouping and no cut.
 * Every row is one shape: the name, its record chips, one detail line
 * (myClients.detailLine; empty when nothing is scheduled) and the note
 * action, + Case Note (the SPICE email summary for a SPICE-only viewer). There is no section heading: the page is My Clients, so the
 * count line and the All clients link head the list.
 */
import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/primitives/Button';
import { Chip } from '@/components/primitives/Chip';
import { NavArrowLink } from '@/components/home/NavArrowLink';
import type { CmHomeClientWire } from '@/lib/cmHomeApi';
import { clientPhotoUrl } from '@/lib/clientsApi';
import { writeSpiceVisitPrefill } from '@/lib/spiceVisitPrefill';
import { initials, spiceRowChipText } from '@/lib/cmHome';
import { countPhrase, detailLine, stayChip } from '@/lib/myClients';
import { byDisplayName } from '@/lib/clientOrder';

function RowAvatar({ name, photoUrl }: { name: string; photoUrl: string | null }): ReactNode {
  const [errored, setErrored] = useState(false);
  if (photoUrl !== null && !errored) {
    return (
      <img
        src={photoUrl}
        alt=""
        loading="lazy"
        data-testid="client-avatar-img"
        className="h-8 w-8 shrink-0 rounded-pill bg-sage-100 object-cover"
        onError={() => setErrored(true)}
      />
    );
  }
  return (
    <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-pill bg-sage-50 text-xs font-bold text-sage-800">
      {initials(name)}
    </span>
  );
}

export interface MyClientsListProps {
  clients: readonly CmHomeClientWire[];
  todayIso: string;
  tenantSlug: string;
  showAllClientsLink: boolean;
  canWriteClinical: boolean;
  /** A SPICE-only viewer's Note opens the SPICE email summary for the client. */
  spiceOnlyViewer: boolean;
  /** "+ Visit": opens the visit door (LP-12's dialog) for the client. */
  onVisit?: (clientId: string) => void;
}

/** One fixed row shape; the test holds every row to the same classes. */
/** One fixed row shape; the test holds every row to the same classes.
 *  Content centred in the bar; a plainly visible hover; a focus ring for
 *  the keyboard. */
const ROW_CLASS =
  'flex cursor-pointer items-center gap-3 px-1 py-2.5 hover:bg-sage-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400';

export function MyClientsList({ clients, todayIso, tenantSlug, showAllClientsLink, canWriteClinical, spiceOnlyViewer, onVisit }: MyClientsListProps): ReactNode {
  const navigate = useNavigate();
  // Everyone by first name, in All clients' order (LP-19).
  const ordered = [...clients].sort(byDisplayName);
  // A SPICE-only viewer's roster opens on its SPICE view.
  const allClientsHref = spiceOnlyViewer ? `/t/${tenantSlug}/clients/all?view=spice` : `/t/${tenantSlug}/clients/all`;
  return (
    <section aria-label="My clients" data-testid="clients-band" className="min-w-0">
      {/* The list's head shares the rail headings' box (h-7, mb-2), so the
          first client row and each rail section's first row line up. */}
      <div className="mb-2 flex h-7 items-center justify-between gap-2 px-1">
        <p className="text-title-sm font-semibold text-sage-800" data-testid="my-clients-list-count">
          {countPhrase(ordered.length, 'client', 'clients')}
        </p>
        {showAllClientsLink && (
          <NavArrowLink to={allClientsHref} data-testid="all-clients-link">
            All clients
          </NavArrowLink>
        )}
      </div>
      {ordered.length === 0 ? (
        <div data-testid="clients-empty" className="px-1 py-3 text-sm text-ink-soft">
          {showAllClientsLink ? (
            <p className="m-0">
              No clients are assigned to you yet; when they are, they are listed here. The whole practice is one click away.{' '}
              <NavArrowLink to={allClientsHref} data-testid="browse-all-clients-link">
                Browse all clients
              </NavArrowLink>
            </p>
          ) : (
            "Once you're assigned a client, they will show on this list."
          )}
        </div>
      ) : (
        <ul className="m-0 list-none divide-y divide-sage-100 border-t border-sage-100 p-0">
          {ordered.map((c) => {
            const chip = stayChip(c);
            const goToRecord = (): void => {
              navigate(`/t/${tenantSlug}/clients/${c.id}`);
            };
            const onRowKey = (e: KeyboardEvent<HTMLLIElement>): void => {
              if (e.target !== e.currentTarget) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                goToRecord();
              }
            };
            return (
              <li key={c.id} data-testid="client-row" role="link" tabIndex={0} onClick={goToRecord} onKeyDown={onRowKey} className={ROW_CLASS}>
                <RowAvatar name={c.display_name} photoUrl={c.has_photo ? clientPhotoUrl(tenantSlug, c.id) : null} />
                {/* The name block holds the two lines' height (h-6 + h-5) and
                    centres in it, so a row with no detail line keeps its height
                    and its name sits level with the avatar and the actions. */}
                <div data-testid="client-name-block" className="flex min-h-11 min-w-0 flex-1 flex-col justify-center">
                  {/* One line at desktop widths (the name truncates before a
                      chip moves down); on a phone the chips wrap under the name. */}
                  <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 lg:h-6 lg:flex-nowrap">
                    <span data-testid="client-name" className="min-w-0 max-w-full truncate text-value font-semibold leading-6 text-ink">
                      {c.display_name}
                    </span>
                    {chip && (
                      <Chip variant="caution" className="max-w-full shrink-0 truncate" data-testid={`record-chip-${chip.key}`}>
                        {chip.text}
                      </Chip>
                    )}
                    {c.spice_row && (
                      <Chip variant="neutral" className="min-w-0 max-w-full truncate" data-testid="spice-row-chip">
                        {spiceRowChipText(c.spice_row)}
                      </Chip>
                    )}
                  </div>
                  {/* Every row has the line; an empty one takes no space, and
                      the block's min height keeps the row the same height. */}
                  <p data-testid="client-detail" className="h-5 truncate text-xs leading-5 text-ink-soft empty:hidden">
                    {detailLine(c, todayIso)}
                  </p>
                </div>
                {/* The actions: small ghost buttons in the row's right slot,
                    on one line, never a second line. */}
                <div className="flex shrink-0 items-center gap-1">
                {canWriteClinical && onVisit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="row-visit"
                    onClick={(e) => {
                      e.stopPropagation();
                      onVisit(c.id);
                    }}
                  >
                    + Visit
                  </Button>
                )}
                {canWriteClinical && (
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="row-case-note"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (spiceOnlyViewer) {
                        writeSpiceVisitPrefill(tenantSlug, c.id, { client_display_name: c.display_name, last_plan: null, contact_first_name: null });
                        navigate(`/t/${tenantSlug}/ai-tools/email?client_id=${c.id}`);
                        return;
                      }
                      navigate(`/t/${tenantSlug}/clients/${c.id}?tab=notes`);
                    }}
                  >
                    {/* A phone keeps the name readable: the short label below
                        the small breakpoint, the full one from there up. */}
                    <span className="sm:hidden">{spiceOnlyViewer ? '+ Email' : '+ Note'}</span>
                    <span className="hidden sm:inline">{spiceOnlyViewer ? 'Write the SPICE email summary' : '+ Case Note'}</span>
                  </Button>
                )}
                {/* A dual-role viewer's own SPICE-client row keeps its second
                    door, the SPICE email summary, beside Note. */}
                {canWriteClinical && !spiceOnlyViewer && c.is_spice_client === true && (
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="row-spice-email"
                    onClick={(e) => {
                      e.stopPropagation();
                      writeSpiceVisitPrefill(tenantSlug, c.id, { client_display_name: c.display_name, last_plan: null, contact_first_name: null });
                      navigate(`/t/${tenantSlug}/ai-tools/email?mode=spice&client_id=${c.id}`);
                    }}
                  >
                    Write the SPICE email summary
                  </Button>
                )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
